
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Layout from '../Layout/Layout';
import LoadingSwiper from '../slider/loadindSwiper';
import useDataLoader from '../pageInside/exeemple_page/DataLoader';
import DynamicPageRenderer, { buildSectionPaginationProps } from '../pageInside/exeemple_page/DynamicPageRenderer';
import { PAGE_FACETS, getInfoSourceUpdates } from '../pageInside/exeemple_page/allFacets';
import { prices } from '../pageInside/fixedPrices';
import { FirtIDCategory } from '../../config';
import { creatUserPUBToken, getUserPUBToken } from '../auth';
import Loader1 from '../Layout/imgs/loading3.gif';
import { getPaginationLimit } from '../../utils/paginationSettings';
import PromotionChoiceModal from './roleTokens/PromotionChoiceModal';


const _extractFromSettings = (settings) => {
    if (!settings || typeof settings !== 'object') return null;

    const { sections, facets, id } = settings;

    // ── Priorité 1 : sections[] ──────────────────────────────────────────
    if (Array.isArray(sections) && sections.length > 0) {
        const sorted = [...sections].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

        const resolvedFacets = sorted
            .filter(s => s.sectionId && s.enabled !== false)
            .map(s => s.sectionId);

        const configOverrides = {};
        sorted.forEach(s => {
            if (s.sectionId && s.props && Object.keys(s.props).length > 0) {
                configOverrides[s.sectionId] = { props: s.props };
            }
        });

        if (resolvedFacets.length > 0) return { facets: resolvedFacets, configOverrides, pageId: id || null };
    }

    // ── Priorité 2 : facets[] ────────────────────────────────────────────
    if (Array.isArray(facets) && facets.length > 0) {
        return { facets, configOverrides: {}, pageId: id || null };
    }

    // ── Vide → null ──────────────────────────────────────────────────────
    return null;
};

// ─────────────────────────────────────────────────────────────────────────────
// resolvePageConfig_ — CASCADE STRICTE à 3 niveaux
// ─────────────────────────────────────────────────────────────────────────────
//

const resolvePageConfig = (pageSettings) => {
    const result = _extractFromSettings(pageSettings);
    if (result) return result;

    return { facets: PAGE_FACETS.HOME, configOverrides: {} };
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANT: HOME
// ═══════════════════════════════════════════════════════════════════════════

const Home = (props) => {
    let params   = useParams();
    let navigate = useNavigate();
    const location = useLocation();
    const {
        infoSource,
        laguangeElakay,
        homeStyles,
        nbrArr,
    } = props;

    const token    = infoSource?.token || null;
    let   tokenPub = params.token;
    const locationRequestedRef = useRef(false);
    const [promotionChoiceOpen, setPromotionChoiceOpen] = useState(false);

    useEffect(() => {
        if (
            locationRequestedRef.current ||
            infoSource?.location?.requested ||
            typeof infoSource?.getLocation !== 'function'
        ) {
            return;
        }

        locationRequestedRef.current = true;
        infoSource.getLocation().catch(() => {
            // The footer displays permission and geocoding errors.
        });
    // The location request must run only once while Home starts loading.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Scroll top instantané au changement d'URL ─────────────────────────
    const { pathname } = location;
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'instant' });
    }, [pathname]);

    // The scan resolver reaches Home only when both role promotions are
    // currently valid. Consume the history state immediately so Refresh or
    // Back cannot replay the modal, while keeping the validated attribution
    // token in its existing localStorage location.
    useEffect(() => {
        const roles = location.state?.promotionRoles;
        if (location.state?.showPromotionChoice !== true ||
            !Array.isArray(roles) ||
            !roles.includes('Seller') ||
            !roles.includes('Provider')) return;
        setPromotionChoiceOpen(true);
        navigate('/', { replace: true, state: null });
    }, [location.state, navigate]);

    const choosePromotionRole = useCallback((role) => {
        const pubToken = getUserPUBToken()?.pubToken;
        setPromotionChoiceOpen(false);
        if (!pubToken || !['Seller', 'Provider'].includes(role)) return;
        navigate(`/user-step?t=${encodeURIComponent(pubToken)}&role=${encodeURIComponent(role)}`);
    }, [navigate]);

    // ═════════════════════════════════════════════════════════════════════
    // 0. FILTRES LOCAUX + PUB
    // ═════════════════════════════════════════════════════════════════════
    const [myFilters, setMyFilters] = useState({ filters: {} });
    const [getpub, setPub]          = useState([{ urlImg: Loader1, _id: 1 }]);

    const handlePrice = useCallback((value) => {
        let array = [];
        for (let key in prices) {
            if (prices[key]._id === parseInt(value)) array = prices[key].array;
        }
        return array;
    }, []);

    const handleFilters = useCallback((filters, filterBy) => {
        setMyFilters(prev => {
            const n = { ...prev, filters: { ...prev.filters } };
            n.filters[filterBy] = filterBy === 'price' ? handlePrice(filters) : filters;
            return n;
        });
        // Reset skip global quand les filtres changent
        setSkip(0);
    }, [handlePrice]);

    // ── Promo token ───────────────────────────────────────────────────────
    useEffect(() => {
        if (tokenPub) {
            // Keep the referral token, then show the plain Home URL. Product
            // requests read the token from storage when they are sent, so no
            // document reload (it used to load /userPUB/:token twice, then /).
            creatUserPUBToken(tokenPub, () => navigate('/', { replace: true }));
        }
    }, [tokenPub]);

    // ═════════════════════════════════════════════════════════════════════
    // 1. SKIP GLOBAL (sections non-indépendantes)
    // ═════════════════════════════════════════════════════════════════════
    //
    // Utilisé par productGrid standard, sliders, etc. via useDataLoader.
    // Les sections INDEPENDENT_SECTION_IDS ont leur skip dans
    // pageData.__pagination__[sectionId].skip géré par buildSectionPaginationProps.

    const [skip, setSkip] = useState(0);
    // Admin-configurable via AnimationSettings → General → "Pagination &
    // Limites" (paginationLimits.home) — this is the ONE combined limit for
    // every Home facet fetched together in a single /productsF call.
    const limit = getPaginationLimit(infoSource?.allSettings, 'home', 12);

    // ═════════════════════════════════════════════════════════════════════
    // 2. REF DE SCROLL — grille uniquement
    // ═════════════════════════════════════════════════════════════════════

    const gridRef = useRef(null);

    // ═════════════════════════════════════════════════════════════════════
    // 3. CASCADE — pageSettings_ → PAGE_FACETS_.HOME
    // ═════════════════════════════════════════════════════════════════════

    const pageSettings = infoSource?.allSettings?.pages?.HOME || null;

    const { facets, configOverrides } = useMemo(
        () => resolvePageConfig(pageSettings),
        [pageSettings]
    );
    // Home's productGrid gets a "See More" link to the full New Products page.
    // An admin-configured seeMoreLink (page settings props) still wins.
    const homeConfigOverrides = useMemo(() => ({
        ...configOverrides,
        productGrid: {
            ...configOverrides?.productGrid,
            props: { seeMoreLink: '/new-product', ...configOverrides?.productGrid?.props },
        },
    }), [configOverrides]);
    const autoload_useDataLoader = true ; // Auto-chargement à l'initialisation du composant (skip = 0, filters = {})
     
    // ═════════════════════════════════════════════════════════════════════
    // 4. DATA LOADER
    // ═════════════════════════════════════════════════════════════════════
    //
    // setPageData exposé pour écriture dans __pagination__ par section
    // via buildSectionPaginationProps.

    const {
        pageData,
        setPageData,
        loading,
        mergePageData,
        loadPageData,
        reloadFacets,
    } = useDataLoader({
        facets,
        infoSource,
        token,
        skip,
        limit,
        filters: myFilters.filters,
    });

    // ═════════════════════════════════════════════════════════════════════
    // 5. SYNC infoSource → pageData (sections NONE)
    // ═════════════════════════════════════════════════════════════════════

    useEffect(() => {
        const updates = getInfoSourceUpdates(facets, infoSource);
        if (Object.keys(updates).length > 0) mergePageData(updates);
    }, [infoSource, facets]);

    // Si l'admin change la limite Pagination & Limites pendant que la page
    // est déjà ouverte (infoSource.allSettings rechargé), repartir de la
    // page 1 plutôt que de garder un skip qui ne correspond plus à la
    // nouvelle taille de page.
    useEffect(() => {
        setSkip(0);
    }, [limit]);

    // // ═════════════════════════════════════════════════════════════════════
    // // 6. CHARGEMENT — facets_ / skip / filtres changent
    // // ═════════════════════════════════════════════════════════════════════

    useEffect(() => {
        loadPageData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(facets), JSON.stringify(myFilters.filters), skip]);

    // ═════════════════════════════════════════════════════════════════════
    // 7. firstCategoryId — pour Layout.categoriesID
    // ═════════════════════════════════════════════════════════════════════

    const firstCategoryId = useMemo(() => {
        const cats = infoSource?.elakayCategories || pageData?.elakayCategories;
        if (Array.isArray(cats) && cats.length > 0) {
            return cats[0]?._id || FirtIDCategory;
        }
        return FirtIDCategory;
    }, [infoSource?.elakayCategories, pageData?.elakayCategories]);

    // ═════════════════════════════════════════════════════════════════════
    // 9. FLAGS
    // ═════════════════════════════════════════════════════════════════════

    const P1 = (pageData?.siseProduct || 0) > 0;

    // The category slider's data comes from the app (infoSource.elakayCategories),
    // not from this page's request: when it arrived after the products, the
    // slider appeared above them and pushed the whole page down. The loading
    // view waits for it -- 2.5 s at most, so a slow or failed category request
    // never keeps the page hidden.
    const [categoriesWaited, setCategoriesWaited] = useState(false);
    useEffect(() => {
        const timer = setTimeout(() => setCategoriesWaited(true), 2500);
        return () => clearTimeout(timer);
    }, []);
    const awaitingCategories = !categoriesWaited
        && facets.includes('elakayCategories')
        && !(pageData?.elakayCategories?.length > 0);
infoSource.divRef = gridRef; // Expose gridRef to infoSource for potential use in ProductGridPage or autres sections indépendantes
    // ═════════════════════════════════════════════════════════════════════
    // 10. RENDER
    // ═════════════════════════════════════════════════════════════════════

    return (
        <>
        <PromotionChoiceModal
            infoSource={infoSource}
            show={promotionChoiceOpen}
            onClose={() => setPromotionChoiceOpen(false)}
            onSelect={choosePromotionRole}
        />
        <Layout
            infoSource={infoSource}
            categoriesID={firstCategoryId}
            pubCategoryId={FirtIDCategory}
            titleElakay="home"
            loading={loading || awaitingCategories}
            prices={prices}
            handleFilters={handleFilters}
            setMyFilters={setMyFilters}
            getpub={infoSource.getpub}
            SliderPunShow={true}
            outOffPage={false}
            P1={P1}
            {...infoSource?.allSettings?.layout}
        >
            <div style={infoSource.styleNav}>
                <DynamicPageRenderer
                    facets={facets}
                    pageId="HOME"
                    pageData={pageData}
                    infoSource={infoSource}
                    laguangeElakay={laguangeElakay}
                    homeStyles={homeStyles}
                    nbrArr={nbrArr}
                    reloadFacets={reloadFacets}
                    configOverrides={homeConfigOverrides}
                    gridRef={gridRef}
                />
                {loading && !pageData?.productsByArrival?.length && (
                    <>
                        <LoadingSwiper myStyles={homeStyles} />
                        <LoadingSwiper myStyles={homeStyles} />
                        <LoadingSwiper myStyles={homeStyles} />
                        <LoadingSwiper myStyles={homeStyles} />
                    </>
                )}
            </div>
        
        </Layout>
        </>
    );
};

export default Home;
